import { useState, useEffect, useCallback } from 'react'
import { api } from '../api/client.js'

export interface DropdownOption {
  id: string
  name: string
}

interface DropdownData {
  types: DropdownOption[]
  statuses: DropdownOption[]
  states: DropdownOption[]
  categories: DropdownOption[]
  subcategories: DropdownOption[]
  priorities: DropdownOption[]
  impacts: DropdownOption[]
  urgencies: DropdownOption[]
  complexities: DropdownOption[]
  approvalStates: DropdownOption[]
  approvalLevels: DropdownOption[]
  campaigns: DropdownOption[]
  locations: DropdownOption[]
  branches: DropdownOption[]
  departments: DropdownOption[]
  teams: (DropdownOption & { campaignId?: string | null })[]
  agents: { agentId: string; label: string; teamIds: string[] }[]
}

const EMPTY: DropdownData = {
  types: [],
  statuses: [],
  states: [],
  categories: [],
  subcategories: [],
  priorities: [],
  impacts: [],
  urgencies: [],
  complexities: [],
  approvalStates: [],
  approvalLevels: [],
  campaigns: [],
  locations: [],
  branches: [],
  departments: [],
  teams: [],
  agents: [],
}

export function useReassignmentDropdowns() {
  const [data, setData] = useState<DropdownData>(EMPTY)
  const [loading, setLoading] = useState(false)

  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const [
        typesRes,
        statusesRes,
        statesRes,
        categoriesRes,
        prioritiesRes,
        impactsRes,
        urgenciesRes,
        complexitiesRes,
        approvalStatesRes,
        approvalLevelsRes,
        campaignsRes,
        assignableRes,
      ] = await Promise.all([
        api.get<{ types: DropdownOption[] }>('/admin/conversation-management/types/dropdown').catch(() => ({ types: [] })),
        api.get<{ status: DropdownOption[] }>('/admin/conversation-management/status/dropdown').catch(() => ({ status: [] })),
        api.get<{ states: DropdownOption[] }>('/admin/conversation-management/states/dropdown').catch(() => ({ states: [] })),
        api.get<{ categories: DropdownOption[] }>('/admin/conversation-management/categories/dropdown').catch(() => ({ categories: [] })),
        api.get<{ priorities: DropdownOption[] }>('/admin/conversation-management/priorities/dropdown').catch(() => ({ priorities: [] })),
        api.get<{ impacts: DropdownOption[] }>('/admin/conversation-management/impacts/dropdown').catch(() => ({ impacts: [] })),
        api.get<{ urgencies: DropdownOption[] }>('/admin/conversation-management/urgencies/dropdown').catch(() => ({ urgencies: [] })),
        api.get<{ complexities: DropdownOption[] }>('/admin/conversation-management/complexities/dropdown').catch(() => ({ complexities: [] })),
        api.get<{ approvalStates: DropdownOption[] }>('/admin/conversation-management/approval-states/dropdown').catch(() => ({ approvalStates: [] })),
        api.get<{ approvalLevels: DropdownOption[] }>('/admin/conversation-management/approval-levels/dropdown').catch(() => ({ approvalLevels: [] })),
        api.get<{ campaigns: DropdownOption[] }>('/campaigns/dropdown').catch(() => ({ campaigns: [] })),
        api.get<{ agents: { agentId: string; label: string; teamIds: string[] }[]; teams: DropdownOption[] & { id: string; name: string; campaignId: string | null }[] }>(
          '/conversations/assignable-agents',
        ).catch(() => ({ agents: [], teams: [] })),
      ])

      setData({
        types: typesRes.types || [],
        statuses: (statusesRes as any).status || (statusesRes as any).statuses || [],
        states: statesRes.states || [],
        categories: categoriesRes.categories || [],
        subcategories: [],
        priorities: prioritiesRes.priorities || [],
        impacts: impactsRes.impacts || [],
        urgencies: urgenciesRes.urgencies || [],
        complexities: complexitiesRes.complexities || [],
        approvalStates: approvalStatesRes.approvalStates || [],
        approvalLevels: approvalLevelsRes.approvalLevels || [],
        campaigns: campaignsRes.campaigns || [],
        locations: [],
        branches: [],
        departments: [],
        teams: (assignableRes.teams || []).map((t: any) => ({ id: t.id, name: t.name, campaignId: t.campaignId ?? null })),
        agents: assignableRes.agents || [],
      })
    } catch (e) {
      console.error('Failed to fetch dropdown data:', e)
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchSubcategories = useCallback(async (categoryId: string): Promise<DropdownOption[]> => {
    try {
      const res = await api.get<{ subcategories: DropdownOption[] }>(
        `/admin/conversation-management/subcategories/dropdown?categoryId=${categoryId}`,
      )
      return res.subcategories || []
    } catch {
      return []
    }
  }, [])

  useEffect(() => {
    void fetchAll()
  }, [fetchAll])

  return { data, loading, fetchSubcategories, refetch: fetchAll }
}
